"use client";
import type { FC } from "react";
import { useRouter } from "next/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import { TrainingCard } from "../Common/TrainingCard";
import { useGetRecommendedFormations } from "@/actions/formation";
import { toBookmark } from "@/lib/bookmarks";
import type { IFormationDetail } from "@/types/formation";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface RecommendedFormationsProps {
  formation: IFormationDetail;
}

/* Shared by the skeleton and the loaded state. */
const SECTION_CLASS =
  "pt-6 md:pt-15 md:pb-5 pb-2 overflow-hidden px-4 scroll-mt-36 lg:scroll-mt-24";

const HEADING_CLASS =
  "text-center font-red-hat font-light text-[30px] md:text-[40px] leading-10 md:leading-13.25 text-[#1B365E] mb-5 md:mb-8";

const RecommendedFormations: FC<RecommendedFormationsProps> = ({ formation }) => {
  const router = useRouter();
  const {
    recommendedFormations,
    recommendedFormationsLoading,
    recommendedFormationsEmpty,
  } = useGetRecommendedFormations(formation.slug);

  if (recommendedFormationsLoading) {
    return (
      <section id="recommended" className={SECTION_CLASS}>
        <div className="max-w-326.5 mx-auto w-full">
          <h2 className={HEADING_CLASS}>Ce qui pourrait aussi vous intéresser</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className={`h-75.75 bg-[#EFEFEF] animate-pulse rounded-3xl ${
                  i === 1 ? "hidden sm:block" : i > 1 ? "hidden lg:block" : ""
                }`}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Nothing to recommend — skip the section rather than show an empty widget.
  if (recommendedFormationsEmpty) {
    return null;
  }

  return (
    <section id="recommended" className={SECTION_CLASS}>
      <div className="max-w-326.5 mx-auto w-full">
        <h2 className={HEADING_CLASS}>Ce qui pourrait aussi vous intéresser</h2>

        {/* Hard breaks replaced by a measure cap: the forced lines only landed
            correctly at one window width. */}
        <p className="md:block hidden text-center text-[#1D1D1D] text-[16px] lg:text-[18px] leading-7 lg:leading-8 font-medium mb-8 md:mb-10 max-w-4xl mx-auto">
          Prêt à aller encore plus loin ? Découvrez une sélection de formations
          qui pourraient également vous intéresser et compléter votre parcours.
          Nous vous recommandons de découvrir ces formations complémentaires, qui
          vous apporteront un atout supplémentaire dans le développement de
          nouvelles compétences.
        </p>

        <div className="relative xl:pe-17 xl:ps-5">
          {/* Swiper Slider */}
          <Swiper
            modules={[Pagination, Navigation]}
            pagination={{ clickable: true }}
            navigation={{
              nextEl: ".top-next",
              prevEl: ".top-prev",
            }}
            slidesPerView={1.1}
            spaceBetween={24}
            breakpoints={{
              768: { slidesPerView: 2.2, spaceBetween: 24 },
              1024: { slidesPerView: 3, spaceBetween: 20 },
              1280: {
                slidesPerView: 4,
                spaceBetween: 20,
                pagination: { enabled: false },
              },
            }}
            className="pb-15! recomended-swiper"
          >
            <style jsx global>{`
              /* Keep slides flexible without breaking Swiper Grid layout */
              .recomended-swiper .swiper-slide {
                height: auto !important;
                display: flex !important;
                flex-direction: column !important;
              }
              :global(.top-next),
              :global(.top-prev) {
                  color: #1B365E !important;
                  transition: color 0.3s;
              }
              :global(.top-next.swiper-button-disabled),
              :global(.top-prev.swiper-button-disabled) {
                  color: #ABABAB !important;
                  opacity: 1 !important;
                  cursor: not-allowed !important;
              }
            `}</style>
            {recommendedFormations.map((card) => (
              <SwiperSlide key={card.id} className="flex! flex-col h-full">
                <TrainingCard
                  title={card.title}
                  description={card.description}
                  category={card.category}
                  location={card.location}
                  duration={card.duration}
                  price={card.price}
                  financing={card.financing ?? ""}
                  isNew={card.isNew}
                  formation={toBookmark(card)}
                  onDiscover={() => router.push(`/formation/${card.slug}`)}
                  onSubscribe={() => router.push(`/formation/${card.slug}`)}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Navigation Arrows — icon-only, so they need their own labels */}
          <div className="hidden xl:flex flex-col gap-5 absolute right-0 top-3 z-20">
            <button
              type="button"
              aria-label="Formations suivantes"
              className="top-next inline-flex size-11 items-center justify-center cursor-pointer transition-transform hover:scale-110"
            >
              <svg width="28" height="30" viewBox="0 0 28 30" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Formations précédentes"
              className="top-prev inline-flex size-11 items-center justify-center cursor-pointer transition-transform hover:scale-110"
            >
              <svg width="28" height="30" viewBox="0 0 28 30" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="rotate-180">
                <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecommendedFormations;
